/**
 * Chains the two vendors into one "Donate & Enter" flow:
 *
 *   1. The visitor fills Probability's form and submits it.
 *   2. Probability validates as usual, but NOTHING is posted to their API.
 *   3. GoFundMe's embedded checkout opens.
 *   4. Only once the donation completes is the entry posted to Probability,
 *      which then renders its own thank-you state.
 *
 * If the visitor closes the checkout without donating, the entry is never sent
 * and the form is handed back to them intact.
 *
 * HOW THE DEFERRAL WORKS
 *
 * Probability's `handleSubmit` validates every field, collects UTMs, the
 * fingerprint and the referrer, flips the button into its loading state, and
 * only then calls `handleSendForm()` — which is the one method that talks to
 * their API and, on a 200, sets `sw = 1` to show the thank-you page.
 *
 * So `handleSendForm` is the single seam this needs. We shadow it on the
 * element instance, keep the prototype's version, and call that later. Nothing
 * about their validation, their payload or their thank-you page is
 * reimplemented — the send is simply postponed. If their internals ever change
 * shape, `patchForm` finds no method to shadow, logs nothing, and the form goes
 * back to submitting immediately: degraded, not broken.
 *
 * WHAT COULD NOT BE TESTED HERE
 *
 * GoFundMe only frames the checkout on origins allowlisted for the campaign, so
 * on any host that is not on that list the modal opens empty and never posts a
 * completion message. Testing this therefore needs an allowlisted domain — see
 * README "Before this can go live".
 *
 * Verified in a browser on an allowlisted host: the checkout renders, the deferral
 * holds (`sw` stays 0 with the modal open), and cancelling restores the form. The
 * completion path is wired to their documented message and needs one real donation
 * to confirm end to end.
 *
 * That claim about cancelling was WRONG on an allowlisted host, and the shape of
 * the mistake is worth keeping: the two hosts close the modal by different
 * mechanisms, and the check only recognised the one a local host uses. Reported
 * 2026-08-17 as a spinner that never stops after dismissing the checkout; see
 * `isModalShowing`.
 */

import { trackEvent } from "./analytics";

/**
 * GA4 events for the flow, sent from the four points where it can be seen:
 * submit (validation passed), checkout_open, donation_complete, checkout_cancel.
 *
 * They live here and not in `analytics-events.ts` because these moments are not
 * in the DOM. A click on Probability's submit is not a submission — it may fail
 * validation — and a completed donation is a postMessage from GoFundMe with no
 * visible counterpart at all. Nothing here reads a donor field: the only values
 * sent are the amounts GoFundMe already publishes to this page.
 *
 * Together they are the funnel the campaign is judged on:
 *
 *     form_start → donate_submit → checkout_open → donation_complete
 *                                               ↘ checkout_cancel
 */

/** GoFundMe embedded checkout SDK for Blood Cancer United. */
export const GOFUNDME_SDK = "https://giving.gofundme.com/embedded/api/checkout/sdk/js/33874";

/**
 * "Driving for a Dream", campaign 828116. The SDK discovers it by scanning the
 * page for an anchor whose href carries `?campaign=`, which is why donate.ts
 * renders one.
 *
 * DO NOT RECORD THEIR AMOUNTS OR STEPS HERE. The presets, the preselected
 * amount, the frequency options and the number of screens between the amount and
 * the payment methods are all BCU's campaign config on GoFundMe's side. They
 * change with no deploy of ours, and they already have — a comment here once
 * listed presets that had been wrong for weeks. Read the live checkout when you
 * need the current values; see README, "The campaign config is variable".
 *
 * None of it reaches this module: the deferral keys off their close and complete
 * messages, never off amounts or how many screens they render.
 */
export const GOFUNDME_CAMPAIGN = "828116";

/**
 * Origins the embedded checkout posts from. Compared exactly — GoFundMe's own
 * listener does a `startsWith` against full URLs, which would also accept
 * `https://giving.gofundme.co`.
 */
const TRUSTED_ORIGINS = new Set(["https://giving.gofundme.com", "https://giving.classy.org"]);

/** Their message types. Only these three matter to us. */
const DONATION_COMPLETED = "DONATION_COMPLETED_MSG_FROM_APP";
const DONATION_CLOSE_CONFIRMED = "DONATION_CLOSE_CONFIRMED_MSG_FROM_APP";
/** Fires on every edit in their form, carrying the running donation params. */
const DONATION_CHANGED = "DONATION_CHANGED_MSG_FROM_APP";
/**
 * Their analytics stream. Carries the only copy of the settled figures, under
 * `payload.event === "purchase"` — see AMOUNT_FIELDS.
 */
const ANALYTICS_EVENT = "ANALYTICS_EVENT_MSG_FROM_APP";

/** The bits of Probability's element this module touches. */
interface ProbabilityForm extends HTMLElement {
  handleSendForm?: () => void;
  loading?: boolean;
}

/** Marks an element instance as already patched. */
const PATCHED = Symbol("vv-donate-flow");

/**
 * WHERE THE FIGURES COME FROM, AND WHY THE OBVIOUS KEYS ARE WRONG
 *
 * `DONATION_COMPLETED_MSG_FROM_APP` carries an EMPTY payload — literally `{}`.
 * It announces that a donation happened and nothing else, which is why the first
 * live donation stored four blank fields.
 *
 * Everything we get rides on the `purchase` analytics event, which arrives
 * immediately BEFORE the completion message:
 *
 *     ANALYTICS_EVENT_MSG_FROM_APP
 *       payload.event = "purchase"
 *       payload.data  = { value, fees_covered, status, transaction_id,
 *                         displayed_donation_amount, displayed_overhead_amount,
 *                         displayed_total_gross_amount, normalized_*, ... }
 *
 * Two of those names are traps, both confirmed against a $2 donation with the
 * fee covered (transaction 190369620, GoFundMe charged $2.15):
 *
 *   - `displayed_donation_amount` was 2.15, NOT 2. It is the gross — donation
 *     plus any covered fee — so it is a source for the TOTAL, never for the
 *     donation.
 *   - `displayed_overhead_amount` was 0. "Overhead" is GoFundMe's optional tip
 *     to themselves, a different thing from the processing fee, and it is 0 on
 *     this campaign. It is never the fee.
 *
 * With the fee NOT covered all three figures are equal, so a no-fee donation
 * cannot tell these apart — which is exactly how the wrong mapping survived the
 * first two tests.
 *
 * The true breakdown exists only in `POST /api/checkout/finalize-order`
 * (`order.donorCoveredFees = { raw_initial_gross_amount: 2, fee: 0.15,
 * totalAmount: 2.15, isChecked: true }`) and in its response. Both are issued by
 * the framed app on `giving.gofundme.com`, so the same-origin policy puts them
 * out of reach. Hence the derivation in `deriveFigures`.
 */

/** The gross actually charged. Every one of these is the same number. */
const TOTAL_KEYS = [
  "displayed_total_gross_amount",
  "normalized_total_gross_amount",
  "raw_total_gross_amount",
  "displayed_donation_amount",
  "value",
] as const;

/**
 * The donation before any covered fee. Present in the finalize-order vocabulary
 * but NOT in the purchase event, so this normally misses and the donor's own
 * chosen amount (from DONATION_CHANGED) is what answers.
 */
const BASE_KEYS = ["raw_initial_gross_amount", "rawInitialGrossAmount"] as const;

/** The processing fee, under the names finalize-order uses. Also usually absent. */
const FEE_KEYS = ["raw_adjustment_amount", "fee_on_top_amount", "fee"] as const;

/** Whether the donor chose to cover the fee. */
const COVERED_KEYS = ["fees_covered", "is_donor_covered_fee", "isChecked"] as const;

/** GoFundMe's transaction id — the only reliable join back to their records. */
const TRANSACTION_KEYS = ["transaction_id", "transactionId"] as const;

/** The donor's chosen amount, as they typed it: `payload.params.amount`. */
const CHOSEN_KEYS = ["amount"] as const;

/**
 * Money is posted as a two-decimal string — "1.00", not "1".
 *
 * Not cosmetic. Probability's backend coerces a submitted "1" to true and "0" to
 * false, so a $1 donation with no fee covered stored as `raw_amount: yes`,
 * `fee: no`, `total_amount: yes`. Observed on 2026-08-07: `transaction_id` came
 * through intact in the same submission precisely because it is the one value
 * that does not look boolean.
 *
 * The bug only bites on amounts of exactly 0 or 1 — a $250 donation would have
 * stored `raw_amount` correctly and mangled only the zero fee, which is a far
 * more confusing thing to find later. Two decimals is also simply what a
 * currency amount should look like.
 */
function asMoney(value: unknown): string {
  const n = Number(value);
  return Number.isFinite(n) ? n.toFixed(2) : String(value);
}

/**
 * First value found under any of `names`, at any depth. Depth-first and
 * first-match-wins, which is why the more specific spellings lead each list.
 */
function findValue(source: unknown, names: readonly string[], depth = 0): unknown {
  if (depth > 6 || source === null || typeof source !== "object") return undefined;
  const object = source as Record<string, unknown>;
  for (const name of names) {
    const value = object[name];
    if (value !== undefined && value !== null) return value;
  }
  for (const nested of Object.values(object)) {
    const found = findValue(nested, names, depth + 1);
    if (found !== undefined) return found;
  }
  return undefined;
}

/** First of `names` found in any source, in order. */
function pick(sources: readonly unknown[], names: readonly string[]): unknown {
  for (const source of sources) {
    const value = findValue(source, names);
    if (value !== undefined) return value;
  }
  return undefined;
}

/** A finite number, or undefined — so `0` survives and `""`/null do not. */
function num(value: unknown): number | undefined {
  if (value === undefined || value === null || value === "") return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

interface Amounts {
  /** The donation the donor chose, before any covered fee. */
  base: number | undefined;
  /** The covered fee: GoFundMe's own figure when they send one, else the difference. */
  fee: number | undefined;
  /** The gross actually charged. */
  total: number | undefined;
  /** Whether the donor covered the fee. Raw — not yet coerced to a boolean. */
  covered: unknown;
  /** GoFundMe's transaction identifier. Raw — not yet coerced to a string. */
  transaction: unknown;
}

/**
 * The five values, worked out from what GoFundMe actually gives us — as numbers,
 * before anything is formatted.
 *
 * `total` is read directly — every source agrees on the gross. `base` is the
 * donation the donor chose, which the purchase event does not carry, so it comes
 * from the last DONATION_CHANGED. The fee is then the difference.
 *
 * Subtracting is not inventing a figure: both numbers are GoFundMe's own, and
 * `total - base` is the definition of the covered fee. It is only used when
 * their explicit fee key is absent, which is the normal case. Rounded to cents
 * because floating point makes 2.15 - 2 into 0.14999999999999991.
 *
 * Split out of `deriveFigures` so the GA4 event can report numbers while the
 * entry keeps its two-decimal strings. Both callers read the SAME values from
 * the SAME sources, which is the property that matters: a report that disagreed
 * with Probability's records would be worse than no report at all.
 */
function deriveAmounts(sources: readonly unknown[]): Amounts {
  const total = num(pick(sources, TOTAL_KEYS));
  const base = num(pick(sources, BASE_KEYS)) ?? num(pick(sources, CHOSEN_KEYS));
  const explicitFee = num(pick(sources, FEE_KEYS));
  const fee =
    explicitFee ??
    (total !== undefined && base !== undefined
      ? Math.round((total - base) * 100) / 100
      : undefined);

  return {
    base,
    fee,
    total,
    covered: pick(sources, COVERED_KEYS),
    transaction: pick(sources, TRANSACTION_KEYS),
  };
}

/**
 * The five values as the entry carries them: two-decimal money strings, a real
 * boolean, a string id.
 *
 * Anything that cannot be established is left undefined and simply not written,
 * so a missing figure never overwrites a field with a wrong one.
 */
function deriveFigures(sources: readonly unknown[]): Record<string, string | boolean> {
  const { base, fee, total, covered, transaction } = deriveAmounts(sources);

  const figures: Record<string, string | boolean> = {};
  if (base !== undefined) figures.raw_amount = asMoney(base);
  if (fee !== undefined && fee >= 0) figures.fee = asMoney(fee);
  if (total !== undefined) figures.total_amount = asMoney(total);
  if (covered !== undefined) figures.p_is_checked_p = Boolean(covered);
  if (transaction !== undefined) figures.transaction_id = String(transaction);
  return figures;
}

/**
 * The object Probability actually posts. It is module-scoped in their bundle, so
 * the only handle on it is the `.result` property they pass down to every field
 * component — the same reference `handleSendForm` reads at send time.
 */
function resultOf(form: ProbabilityForm): Record<string, unknown> | null {
  for (const field of form.querySelectorAll<HTMLElement & { result?: unknown }>("#myForm > *")) {
    const result = field.result;
    if (result && typeof result === "object") return result as Record<string, unknown>;
  }
  return null;
}

/**
 * Copies the donation figures into the entry.
 *
 * `sources` are consulted in order, first hit wins: the `purchase` analytics
 * event (everything), then the completion payload (empty today, but it costs
 * nothing to keep reading in case they ever fill it), then the last
 * DONATION_CHANGED (the donation only, no fees).
 *
 * Writing to `result` is the part that matters: their validation collected every
 * field's value back at submit time, long before the donation existed, so the
 * inputs alone would arrive empty. The DOM is written too, only to keep the two
 * in step.
 *
 * If nothing carries the figures the entry still goes through — the donation
 * happened, and losing the amounts is not a reason to lose the entry.
 */
function recordAmounts(form: ProbabilityForm, sources: readonly unknown[]): void {
  const result = resultOf(form);
  let matched = 0;

  // Kept deliberately: these figures are only ever seen once, at the moment a
  // real donation lands, and reconstructing them afterwards means reading
  // GoFundMe's books. `transaction_id` in here is what reconciles an entry
  // against their transaction record.
  console.info("[valvoline-donate] donation sources:", sources);

  const figures = deriveFigures(sources);
  console.info("[valvoline-donate] figures:", figures);

  for (const [name, posted] of Object.entries(figures)) {
    // Absent means the program config dropped the field; do not invent a key.
    // Every field's id is its config name, the checkbox included.
    const input = form.querySelector<HTMLInputElement>(`#${name}`);
    if (!input) continue;

    matched += 1;
    if (typeof posted === "boolean") input.checked = posted;
    else input.value = posted;
    if (result) result[name] = posted;
  }

  if (matched === 0) {
    console.warn("[valvoline-donate] no donation figures found in:", sources);
  }
}

/**
 * The completed donation, as GA4 sees it.
 *
 * Reads `deriveAmounts`, the same source `deriveFigures` uses for the entry, so
 * the reported numbers and Probability's records can never disagree. `value` is
 * the gross the donor was actually charged, which is the figure GoFundMe will
 * reconcile against.
 *
 * THE MONETARY PARAMETERS ARE SENT AS NUMBERS, NOT STRINGS. The entry wants
 * two-decimal text (`asMoney`), because that is what a currency amount should
 * look like in Probability's records; GA4 wants a number, because a custom
 * METRIC registered against a string parameter does not aggregate — it collects
 * the hits and sums nothing. Do not "tidy" these back into `figures.*`: the
 * strings are correct for the entry and wrong for the report.
 *
 * `transaction_id` is included so a GA4 row can be tied back to one GoFundMe
 * transaction. It identifies a payment, not a person: no field the donor typed
 * into Probability's form is ever sent here.
 *
 * The event is NOT named `purchase`. GA4 reserves that for ecommerce and expects
 * an `items` array with it; sending a bare value would seed the monetisation
 * reports with a half-populated purchase that no one can then untangle.
 */
function trackDonation(sources: readonly unknown[]): void {
  const { base, fee, total, covered, transaction } = deriveAmounts(sources);

  trackEvent("donation_complete", {
    provider: "gofundme",
    section: "donate",
    currency: "USD",
    // Gross preferred, chosen amount as the fallback — same order the entry uses.
    value: total ?? base,
    donation_amount: base,
    // Same `fee >= 0` guard the entry applies: a negative fee is a figure we
    // failed to derive, not a discount, and reporting it would skew the metric.
    fee_amount: fee !== undefined && fee >= 0 ? fee : undefined,
    fees_covered: covered !== undefined ? Boolean(covered) : undefined,
    transaction_id: transaction !== undefined ? String(transaction) : undefined,
  });
}

function submitButtonOf(root: ParentNode): HTMLButtonElement | null {
  return root.querySelector<HTMLButtonElement>("#btn-next");
}

/**
 * Hands the form back after a cancelled checkout. `handleSubmit` disabled the
 * button and set `loading` before we intercepted, and neither is undone by
 * Probability unless a response arrives.
 */
function releaseForm(form: ProbabilityForm): void {
  form.loading = false;
  const button = submitButtonOf(form);
  if (!button) return;
  button.disabled = false;
  button.classList.remove("loading", "loading-translation");
}

/**
 * Whether this `eg-modal` is the one currently on screen.
 *
 * The SDK puts the modal away in one of two ways, and they leave different
 * traces, so both have to be recognised:
 *
 *   - `_removeView()` takes the element out of the document entirely. That is
 *     what happens when the campaign never became ready — i.e. on any domain
 *     GoFundMe has not allowlisted, which is every local host.
 *   - `_hideViewOnly()` calls `_setViewVisibility(view, "hidden")`, which sets
 *     `display: block` and `visibility: hidden` and adds `aria-hidden`. The
 *     element stays in the document. This is the path on a working host, where
 *     the campaign IS ready — so it is the one that matters in production, and
 *     the one the previous check could not see.
 *
 * Size is deliberately NOT part of this. The element measures 0 tall whether it
 * is open or shut — everything visible is in its shadow root, positioned — so
 * the old height test never even registered the modal as open on a live host,
 * and a ResizeObserver had nothing to fire on when it was hidden at that same
 * height. Locally the element is removed instead, which does report 0, which is
 * exactly why cancelling looked fine in every environment it could be tried in.
 */
function isModalShowing(modal: Element): boolean {
  if (!modal.isConnected) return false;
  const style = getComputedStyle(modal);
  return style.visibility !== "hidden" && style.display !== "none";
}

/**
 * Watches the SDK's modal for closing, and reports the first open -> closed
 * transition.
 *
 * This is the fallback for the close MESSAGE, and the fallback is not the rare
 * path: `DONATION_CLOSE_CONFIRMED_MSG_FROM_APP` is only sent when the framed app
 * has announced `DONATION_CLOSE_INTERCEPT_READY`, since the SDK checks
 * `closeInterceptReadyCampaigns` before asking the app to close and otherwise
 * calls `modal.forceClose()` — which posts nothing to anyone. So a perfectly
 * normal dismissal can produce no message at all, and then this is the only
 * thing standing between the visitor and a spinner that never stops.
 *
 * EVERY `eg-modal` is considered, not the first: the SDK preloads iframes at
 * startup and caches one view per campaign, so `querySelector("eg-modal")` can
 * easily return a hidden preload that never opens or closes. `wasOpen` latches
 * only on something actually visible, so those preloads cannot trigger a cancel.
 *
 * MutationObserver rather than ResizeObserver because the state we care about is
 * published as attributes — `style` and `aria-hidden` on the element, plus its
 * removal from <body> — and a hide leaves every box on the page the same size.
 */
function watchModalClose(onClose: () => void): () => void {
  if (typeof MutationObserver === "undefined") return () => {};

  const showing = (): boolean =>
    Array.from(document.querySelectorAll("eg-modal")).some(isModalShowing);

  let wasOpen = false;
  const check = (): void => {
    if (showing()) {
      wasOpen = true;
    } else if (wasOpen) {
      wasOpen = false;
      onClose();
    }
  };

  const observer = new MutationObserver(check);
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["style", "class", "aria-hidden", "hidden"],
  });

  // The modal is opened by a click we make ourselves, and the SDK sets up its
  // view over several turns; a mutation always follows, so the observer sees the
  // open. The initial check only covers a modal that is somehow already up.
  check();

  return () => observer.disconnect();
}

export function wireDonateFlow(root: HTMLElement): () => void {
  /** The postponed `handleSendForm`, set while the checkout is open. */
  let pendingSend: (() => void) | null = null;
  let stopWatchingModal: (() => void) | null = null;
  /** Payload of the last DONATION_CHANGED, the last-resort source for the figures. */
  let lastChanged: unknown = null;
  /** `data` of the `purchase` analytics event — the figures that actually settled. */
  let purchase: unknown = null;

  const finish = (): void => {
    stopWatchingModal?.();
    stopWatchingModal = null;
  };

  const send = (): void => {
    const run = pendingSend;
    pendingSend = null;
    finish();
    // Their own method: posts the entry and, on a 200, shows the thank-you page.
    run?.();
  };

  const cancel = (form: ProbabilityForm): void => {
    if (!pendingSend) return;
    pendingSend = null;
    finish();
    // Only reached with a send pending, so this is a visitor who validated the
    // form, opened the checkout and left without donating — the one drop-off in
    // this funnel that is ours to fix.
    trackEvent("checkout_cancel", { provider: "gofundme", section: "donate" });
    releaseForm(form);
  };

  const openCheckout = (form: ProbabilityForm): void => {
    const trigger = root.querySelector<HTMLAnchorElement>(".valvoline-donate__checkout");
    if (!trigger) {
      // No way to take payment — send the entry rather than swallow it. Flagged
      // as its own event because the entries it produces have no donation behind
      // them, and nothing else in the data would explain why.
      trackEvent("checkout_unavailable", { provider: "gofundme", section: "donate" });
      send();
      return;
    }
    stopWatchingModal = watchModalClose(() => cancel(form));
    trackEvent("checkout_open", { provider: "gofundme", section: "donate" });
    trigger.click();
  };

  const patchForm = (): void => {
    const form = root.querySelector<ProbabilityForm>("universal-form-webcomponent");
    if (!form || PATCHED in form) return;

    const original = (Object.getPrototypeOf(form) as ProbabilityForm).handleSendForm;
    if (typeof original !== "function") return;

    Object.defineProperty(form, PATCHED, { value: true });
    form.handleSendForm = function patched(this: ProbabilityForm): void {
      // Their validation has already passed by the time this runs, so this is a
      // real submission — GA4's `form_submit`, under a name that says which form.
      trackEvent("donate_submit", {
        form_id: "probability-universal-form",
        form_name: "Donate & Enter",
        section: "donate",
      });
      pendingSend = () => original.call(this);
      openCheckout(this);
    };
  };

  const onMessage = (event: MessageEvent): void => {
    if (!TRUSTED_ORIGINS.has(event.origin)) return;
    const type = (event.data as { type?: string } | null)?.type;
    if (type === ANALYTICS_EVENT) {
      // Arrives immediately before DONATION_COMPLETED, and is the only message
      // that ever carries the fee, the total and the cover-fees choice.
      const analytics = (event.data as { payload?: { event?: string; data?: unknown } }).payload;
      if (analytics?.event === "purchase") purchase = analytics.data;
    } else if (type === DONATION_CHANGED) {
      // Their running tally as the donor types: right about the donation, blind
      // to fees. Last resort if the purchase event ever fails to arrive.
      lastChanged = (event.data as { payload?: unknown }).payload;
    } else if (type === DONATION_COMPLETED) {
      const form = root.querySelector<ProbabilityForm>("universal-form-webcomponent");
      const completed = (event.data as { payload?: unknown }).payload;
      if (form) recordAmounts(form, [purchase, completed, lastChanged]);
      trackDonation([purchase, completed, lastChanged]);
      send();
    } else if (type === DONATION_CLOSE_CONFIRMED) {
      const form = root.querySelector<ProbabilityForm>("universal-form-webcomponent");
      if (form) cancel(form);
    }
  };

  window.addEventListener("message", onMessage);
  patchForm();

  // Their element is upgraded whenever their script finishes loading, and the
  // whole subtree is replaced when the form switches state, so keep re-checking.
  const observer =
    typeof MutationObserver === "undefined" ? null : new MutationObserver(() => patchForm());
  observer?.observe(root, { childList: true, subtree: true });

  return () => {
    window.removeEventListener("message", onMessage);
    observer?.disconnect();
    finish();
  };
}
